import { Button, ClickEvent, ClickListener, DataSourceExecutionEvent, DataSourceMode, Image, Label, Layout, SlideoutDecorator, Snackbar, Tab, Table, TableRow, TableRowDisplayEvent, TableRowMode, TableSelectionEvent, Toast } from "@mcleod/components";
import { AutogenLayoutInvoiceHistoryTabView } from "./autogen/AutogenLayoutInvoiceHistoryTabView";
import { BillingHistoryOverview } from "./BillingHistoryOverview";
import { StopProgress } from "@mcleod/dispatch/src/StopProgress";
import { Api, CompanySettings, Currency, CurrencyProps, CurrencySettings, CurrencyUtil, McLeodClassicIntegration, ModelRow, SortDirection, SortUtil, StringUtil } from "@mcleod/core";
import { LMEUserSettings, UsersResponsibilitySlideoutTrans } from "@mcleod/general";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { ReprintFreightBill } from "./ReprintFreightBill";
import { ViewEdiBalanceDue } from "@mcleod/datafusion/src/ViewEdiBalanceDue";
import { ModelEdiBilling } from "@mcleod/datafusion/src/models/ModelEdiBilling";
import { openEdiBillingSlideout, reloadEdiBilling } from "@mcleod/datafusion/src/EdiBillingUtil";
import { EdiRawDataSlideout } from "@mcleod/datafusion/src/EdiRawDataSlideout";
import { TransitionOptions } from "@mcleod/components/src/base/TransitionOptions";
import { InvoiceProcessingCreditRebillSlideout } from "./InvoiceProcessingCreditRebillSlideout";
import { ViewEdiBillingDetail } from "@mcleod/datafusion";

const ORDER_USER_SHARED_FIELDS = ["operations_user", "agent_payee_id", "entered_user_id", "billing_user_id"];
interface OpenItemFilter {
    id: string
}
export class InvoiceHistoryTabView extends AutogenLayoutInvoiceHistoryTabView {
    private tabOrderChangeEvent: ClickListener = () => this.handleOrderTabChange();
    private tabMovementChangeEvent: ClickListener = () => this.handleMovementTabChange();
    public handleOrderTabChange = () => { };
    public handleMovementTabChange = () => { };
    private _stopsTable: Table;
    private chargePanelExpanded: boolean = true;
    private taxPanelExpanded: boolean = true;
    private _panelEdiBillingExpanded: boolean = true;

    onLoad(): void | Promise<void> {
        this.panelOrderTab.addClickListener(this.tabOrderChangeEvent);
        this.panelMovementTab.addClickListener(this.tabMovementChangeEvent);
        if (!CompanySettings.getSingleton().isAsset()) {
            this.tableOtherCharges.removeColumn(this.tableOtherCharges.columns.find(col => "columnHeaderDriver" === col.headingCell.id));
        }
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            this.panelTaxes.visible = true;
        }
        this.collapse();
    }

    async doAfterExecute(event) {
        const overviewLayout = this.layoutBillingHistoryOverview as BillingHistoryOverview;
        let isYSplit: boolean = false;
        if (this.stopsTable && this.stopsTable.data) {
            (overviewLayout.layoutStopProgress as StopProgress).setStopsTable(this.stopsTable);
            isYSplit = this.stopsTable?.data?.some(row => row.get("stop_type") === "SP" || row.get("stop_type") === "SD");
        } else {
            (overviewLayout.layoutStopProgress as StopProgress).visible = false;
        }
        await overviewLayout.showErrorPanel(isYSplit);
        this.textboxFixedChargeSegAllocCode.visible = this.mainDataSource?.activeRow?.get("seg_alloc_enabled");
    }

    async invoiceActivityOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        const imageDfInvoice = tableRow.findComponentById("imageDFInvoice") as Image;
        const imageDfInvoiceError = tableRow.findComponentById("imageDfInvoiceError") as Image;
        if (modelRow.get("is_split_bill") === "Y") {
            const imageSplitBill = tableRow.findComponentById("imageSplitBill");
            if (imageSplitBill) {
                imageSplitBill.visible = true;
            }
        }
        if (modelRow.get("billing_history.rebilled") === "Y") {
            const imageRebill = tableRow.findComponentById("imageRebill");
            if (imageRebill) {
                imageRebill.visible = true;
            }
        }
        imageDfInvoice.visible = !StringUtil.isEmptyString(modelRow.getString("edi_billing.invoice_no_string")) && modelRow.getNumber("edi_billing.edi_error") == 0;
        imageDfInvoiceError.visible = modelRow.getNumber("edi_billing.edi_error") > 0;
        tableRow.setAdditionalToolProps("buttonReprintFreight", { visible: modelRow.get("record_type") !== "P" && modelRow.get("source") !== "A" })
        tableRow.setAdditionalToolProps("buttonBalanceDue", { visible: modelRow.get("record_type") !== "P" && modelRow.get("source") !== "A" })
    }

    private calculateTotals(sourceData: ModelRow[], fields: string[]): Currency[] {
        return fields.map(field => {
            const total = new Currency(0);
            const orderCurrencyCode = this.sourceOrders.activeRow.get("freight_charge_c", "");
            if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
                total.setCurrencyCode(orderCurrencyCode);
            }
            sourceData.forEach(async row => {
                let amount = row.getCurrency(field);
                if (CurrencySettings.getSingleton().multiCurrencyEnabled()
                    && !StringUtil.isEmptyString(orderCurrencyCode)
                    && amount.getCurrencyCode() !== orderCurrencyCode) {
                    amount = await this.convertAmount(amount, orderCurrencyCode);

                }
                total.add(amount);
            });
            return total;
        });
    }

    async afterExecuteSourceOpenItem(event) {
        const filteredData = this.filterDuplicateEdiBillingRecords();

        this.tableInvoiceActivity.clearRows();

        const numRows = filteredData.length;
        // Use `Promise.all` to handle asynchronous row creation efficiently
        await Promise.all(
            filteredData.map(async (row) => {
                const modelRow = await this.tableInvoiceActivity._createNewRowData();
                modelRow.setValues(row instanceof ModelRow ? row.data : row);
                modelRow._appending = row instanceof ModelRow ? row._appending : false;

                this.tableInvoiceActivity.addRow(modelRow, { mode: TableRowMode.NONE }, {
                    display: true,
                    addToData: this.tableInvoiceActivity.dataSource.isAddingOrUpdating()
                });
            })
        );
        // Update table data source and handle single-row selection
        this.tableInvoiceActivity.dataSource.updateOriginalRowCount();
        this.tableInvoiceActivity.dataSource.preventChangeNotifications = false;

        if (numRows === 1 && !this.tableInvoiceActivity?.rows[0]?.selected) {
            this.tableInvoiceActivity.rows[0].selected = true;
            await this.processSelectedRow(this.tableInvoiceActivity.rows[0]);
        }

        // Calculate totals and update UI
        this.updateTotals(this.tableInvoiceActivity.data);
    }

    private updateTotals(filteredData: ModelRow[]): void {
        const [totalAmountBilled, totalAmountPaid, totalAdjustments, totalOutstandingBalance] =
            this.calculateTotals(filteredData, [
                "amount_billed",
                "amount_paid",
                "adjustments",
                "outstanding_balance"
            ]);

        this.textboxTotalAmountBilled.text = CurrencyUtil.formatCurrency(totalAmountBilled);
        this.textboxTotalAmountPaid.text = CurrencyUtil.formatCurrency(totalAmountPaid);
        this.textboxTotalPaymentAdjustments.text = CurrencyUtil.formatCurrency(totalAdjustments);
        this.textboxTotalOutstandingBalance.text = CurrencyUtil.formatCurrency(totalOutstandingBalance);
    }

    private filterDuplicateEdiBillingRecords(): ModelRow[] {
        const uniqueInvoiceNumbers: OpenItemFilter[] = [];
        return this.sourceOpenItem.data.filter(row => {
            const id = row.getString("id");
            if (uniqueInvoiceNumbers.some(item =>
                item.id === id
            )) {
                return false; // Duplicate, exclude this row
            }
            uniqueInvoiceNumbers.push({ id });
            return true; // Unique, include this row
        }).sort((a: ModelRow, b: ModelRow) => {
            const sortInfo: { fieldName: string, sort: SortDirection }[] = [
                { fieldName: "customer_id", sort: "asc" },
                { fieldName: "bill_date", sort: "asc" }
            ]
            for (const fieldSortInfo of sortInfo) {
                const valueA = SortUtil.getSortTestValue(a, fieldSortInfo.fieldName);
                const valueB = SortUtil.getSortTestValue(b, fieldSortInfo.fieldName);
                const compareResult = SortUtil.compareTwoValues(valueA, valueB, fieldSortInfo.sort, false);
                if (compareResult !== 0)
                    return compareResult;
            }
            return 0;
        });
    }


    private async processSelectedRow(selectedRow: TableRow): Promise<void> {
        if (selectedRow?.data.get("record_type") !== "P" && selectedRow?.data.get("source") !== "A") {
            if (this.hasBillingHistoryId(selectedRow)) {
                const input = {
                    id: selectedRow?.data.get("billing_history.id"),
                    bill_type_input: selectedRow?.data.get("billing_history.bill_type"),
                };
                await this.handleBillingHistorySearchWithInput(input);
            } else if (this.hasRecordTypeAndOrderId(selectedRow)) {
                const input = this.buildSearchInput(selectedRow);
                await this.handleBillingHistorySearchWithInput(input);
            }
        }
        //see if it's an EDI customer
        const customerId = selectedRow?.data.get("customer_id");
        await Api.search("lme/datafusion/edi-customer", { customer_id: customerId, transaction_type: "I" }).then(async ediCustomer => {
            this.tabDatafusion.visible = ediCustomer.data.length > 0;
        });
    }

    // Updated section in onSelectInvoiceActivityTable
    async onSelectInvoiceActivityTable(event: TableSelectionEvent) {
        const tableRow = event.newRow;
        const selectedRow = tableRow?.table?.selectedRow;
        this.panelInvoiceDetails.visible = false;
        await this.processSelectedRow(selectedRow);
    }

    private hasBillingHistoryId(selectedRow: TableRow): boolean {
        return !StringUtil.isEmptyString(selectedRow?.data.get("billing_history.id"));
    }

    private hasRecordTypeAndOrderId(selectedRow: TableRow): boolean {
        return !StringUtil.isEmptyString(selectedRow?.data.get("record_type")) &&
            !StringUtil.isEmptyString(this.sourceOrders?.activeRow.get("id"));
    }

    private buildSearchInput(selectedRow: TableRow): any {
        if (!StringUtil.isEmptyString(selectedRow?.data.get("invoice_no_string"))) {
            return {
                order_id: this.sourceOrders?.activeRow.get("id"),
                invoice_no_string: selectedRow?.data.get("invoice_no_string"),
                bill_type_input: selectedRow?.data.get("record_type"),
            };
        } else {
            return {
                order_id: this.sourceOrders?.activeRow.get("id"),
                bill_type_input: selectedRow?.data.get("record_type"),
                search_null_invoice_no: true,
            };
        }
    }

    private async handleBillingHistorySearchWithInput(input: any) {
        this.panelInvoiceDetails.visible = true;
        this.panelInvoiceDetails.busy = true;
        await this.sourceBillingHistoryDetail.search(input);
        const firstTab = this.tabsetInvoice.components[0] as Tab;
        firstTab.selectAndScroll();
        this.panelInvoiceDetails.busy = false;
    }

    buttonExpandChargesOnClick(event: ClickEvent) {
        const transitionProps = { speed: 100, paddingTop: 0 };
        if (this.chargePanelExpanded) this.panelOtherChargeTable.collapse(transitionProps).then(() => {
            this.buttonExpandCharges.imageRotation = 90
        }); else this.panelOtherChargeTable.expand(transitionProps).then(() => {
            this.buttonExpandCharges.imageRotation = 0
        });
        this.chargePanelExpanded = !this.chargePanelExpanded;
    }

    buttonExpandTaxesOnClick(event: ClickEvent) {
        const transitionProps = { speed: 100, paddingTop: 0 };
        if (this.taxPanelExpanded) this.panelTaxChargeTable.collapse(transitionProps).then(() => {
            this.buttonExpandTaxes.imageRotation = 90
        }); else this.panelTaxChargeTable.expand(transitionProps).then(() => {
            this.buttonExpandTaxes.imageRotation = 0
        });
        this.taxPanelExpanded = !this.taxPanelExpanded;
    }

    afterExecutionBillingHistoryDetail() {
        this.tabLogix.visible = this.sourceBillingHistoryDetail?.activeRow?.getBoolean("is_logix_license", false);
        this.tabInterfaces.visible = this.sourceBillingHistoryDetail?.activeRow?.getBoolean("is_interface_license", false);
        this.setAutoRateContext(this.sourceBillingHistoryDetail.activeRow.get("rate_id"), this.sourceOrders.activeRow.get("orig_dest_rate_id"));
    }

    setAutoRateContext(rateId: string, odRateId: string) {
        if (rateId != null) {
            this.labelAutoRated.visible = true;
            if (odRateId != null) {
                CommonTooltips.setTooltipContentLayout(
                    this.labelAutoRated,
                    odRateId.toString(),
                    "lme/dispatch/rates/OriginDestinationRateQuickInfo"
                );
            }
            else {
                CommonTooltips.setTooltipContentLayout(
                    this.labelAutoRated,
                    rateId.toString(),
                    "lme/dispatch/rates/AutoRateQuickInfo"
                );
            }
        }
        else {
            this.labelAutoRated.visible = false;
        }
    }

    get stopsTable(): Table {
        return this._stopsTable;
    }

    set stopsTable(value: Table) {
        this._stopsTable = value;
    }

    /** This is an event handler for the onClick event of buttonUsers. */
    buttonUsersOnClick(event: ClickEvent) {
        const urs = new UsersResponsibilitySlideoutTrans("lme/dispatch/OrderUsers", this.getOrderUsersSlideoutTitle(), this.getOrderUsersIdFilter(),
            ORDER_USER_SHARED_FIELDS, this.sourceOrders, this.sourceResponsibleHist);
        urs.show();
    }
    private getOrderUsersSlideoutTitle(): string {
        const name = this.sourceOrders.activeRow?.get("id");
        return "Order Users" + ((StringUtil.isEmptyString(name) === false) ? (" - " + name) : "");
    }

    private getOrderUsersIdFilter(): any {
        const id = this.sourceOrders.activeRow?.get("id");
        if (id != null)
            return { id: id };
        return null;
    }


    /** This is an event handler for the onClick event of buttonOpenClassic. */
    buttonOpenClassicOnClick(event: ClickEvent) {
        const orderId = this.mainDataSource.activeRow.get("id");
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.ar.EntryBillingHistory", "<ext> search order_id='" + orderId + "'");
    }

    async buttonReprintFreightOnClick(event: ClickEvent) {
        await this.openReprintSlideout(event, false);
    }

    async buttonBalanceDueOnClick(event: ClickEvent) {
        await this.openReprintSlideout(event, true);
    }

    async openReprintSlideout(event: ClickEvent, isBalanceDue: boolean) {
        const button = event.target as Button;
        const tableRow = this.tableInvoiceActivity.findTableRowFromAdditionalTool(button);
        const modelRow = tableRow.data as ModelRow;
        let includeOnlyInvoices = false;
        if (isBalanceDue) {
            includeOnlyInvoices = await CommonDialogs.showYesNo("Include only invoice(s) with balance?");
            if (includeOnlyInvoices === null || includeOnlyInvoices === undefined) includeOnlyInvoices = false;
        }
        CommonDialogs.showYesNo("Send report to Imaging?").then(result => {
            if (result === null || result === undefined) return;
            const reprintFreightBillProps = {
                isSummaryBill: modelRow.get("customer.summary_billing") === "Y",
                invoiceNumber: modelRow.get("invoice_no_string"),
                orderId: modelRow.get("order_id"),
                customerId: modelRow.get("customer_id"),
                sendToImaging: result,
                billDate: modelRow.get("bill_date"),
                billType: modelRow.get("record_type"),
                isBalanceDue: isBalanceDue,
                includeOnlyInvoices: includeOnlyInvoices
            };
            ReprintFreightBill.openSlideout(reprintFreightBillProps);
        });
    }

    /** This is an event handler for the onClick event of buttonSave. */
    async buttonEditOnClick(event: ClickEvent) {
        const props = {
            sourceEdiBilling: this.sourceEdiBilling, // Ensure this is an instance of DataSource<ModelRow<any>>
            tabErrorsVisible: false,
            sourceEdiBillingMode: DataSourceMode.UPDATE,
            textReadyToSendEnabled: true,
            txtCorrectionIndicatorEnabled: true,
            txtServiceCodeEnabled: true,
            txtBatchEnabled: true,
            switchTransmitEnabled: true
        };
        openEdiBillingSlideout(props);
    }

    openRawDataOnClick(event: ClickEvent) {
        const invoiceId = this.sourceEdiBilling.activeRow?.get("id");
        const orderId = this.sourceEdiBilling.activeRow?.get("order_id");
        const templateType = this.sourceEdiBilling.activeRow?.get("edi_template_type");
        EdiRawDataSlideout.openSlideout({ title: `Outbound Invoice - ${orderId}`, invoiceId, templateType })
    }

    /** This is an event handler for the afterExecution event of sourceEdiBilling. */
    async sourceEdiBillingAfterExecution(event: DataSourceExecutionEvent) {
        // Clear existing rows in the table
        this.tableEdiBalanceDue.clearRows();
        const invoice_no_string = this.sourceEdiBilling.activeRow?.get("invoice_no_string");
        const order_id = this.sourceEdiBilling.activeRow?.get("order_id");
        const balanceDueData = await new ModelEdiBilling().search({
            order_id: order_id,
            is_balance_due: "Y"
        });

        // Clear existing rows in the table
        this.tableEdiBalanceDue.clearRows();

        // Add each row to tableEdiBalanceDue
        balanceDueData.modelRows.forEach(row => {
            this.tableEdiBalanceDue.addRow(row, { mode: TableRowMode.NONE }, { display: true });
        });

        // Update table visibility based on the data
        const hasEdiBillingData = this.sourceEdiBilling.data.length > 0;
        this.tabDatafusion.visible = this.sourceEdiBilling.data.length > 0 || hasEdiBillingData;
        this.tableEdiBalanceDue.visible = hasEdiBillingData;
    }

    /** This is an event handler for the onRowDisplay event of tableEdiBalanceDue. */
    async tableEdiBalanceDueOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data;
        const layoutBalanceDue = tableRow.findComponentById("layoutBalanceDue") as ViewEdiBalanceDue;

        if (layoutBalanceDue) {
            const id = modelRow.get("id");
            await layoutBalanceDue.sourceEdiBilling.search({ id: id }).then(async () => {
                await layoutBalanceDue.loadEdiMapError(id);
                await layoutBalanceDue.loadOtherCharges(id);
                await layoutBalanceDue.loadTaxCharges(id);
                layoutBalanceDue.panelFuncAck.visible = modelRow.get("func_ack_id") != null;
                layoutBalanceDue.labelBalanceDue.text = "Balance Due " + (tableRow.index + 1);
            });
        }
    }
    /** This is an event handler for the onClick event of buttonExpand. */
    buttonExpandOnClick(event: ClickEvent) {
        if (this._panelEdiBillingExpanded === true) {
            this.collapse();
        }
        else {
            this._panelEdiBillingExpanded = true;
            this.setChevronImageRotation();
            this.expand();
        }
    }
    private setChevronImageRotation() {
        this.buttonExpand.imageRotation = this._panelEdiBillingExpanded ? 0 : 90;
    }
    public override collapse(options?: TransitionOptions): Promise<any> {
        this._panelEdiBillingExpanded = false;
        this.setChevronImageRotation();
        return this.panelCollapsible.collapse({ speed: 400, ...options });
    }

    public override async expand(options?: TransitionOptions) {
        this.panelCollapsible.expand({ speed: 400, ...options });
    }

    /** This is an event handler for the onClick event of buttonReload. */
    buttonReloadOnClick(event: ClickEvent) {
        reloadEdiBilling(this.sourceEdiBilling);
    }

    buttonOpenInvoiceOnClick(event: ClickEvent) {
        if (this.sourceEdiBilling.activeRow) {
            ViewEdiBillingDetail.navigateTo({ key: this.sourceEdiBilling.activeRow.get("id"), newTab: true });
        }
    }

    /** This is an event handler for the onClick event of buttonCreditRebill. */
    buttonCreditRebillOnClick(event: ClickEvent) {
        const layout = Layout.getLayout("lme/ar/InvoiceProcessingCreditRebillSlideout", {
            width: window.innerWidth * .75,
            backgroundColor: "defaultBackground",
            fillHeight: true,
            paddingBottom: 50
        }) as InvoiceProcessingCreditRebillSlideout;

        new SlideoutDecorator({
            layout,
            title: "Credit/Rebill",
            fillVerticalSpace: true,
            addlComponents: [layout.buttonNextStep],
            doAfterSlideIn: () => {
                if (this.sourceBillingHistory.data.length > 0 && this.sourceBillingHistory.data[this.sourceBillingHistory.data.length - 1]?.get("is_summary_bill") === "Y") {
                    layout.textboxRebillBy.selectedItem = layout.textboxRebillBy.items[1];
                    const invoiceNo = this.sourceBillingHistory.data[this.sourceBillingHistory.data.length - 1]?.get("invoice_no_string");
                    layout.textboxNumber.text = invoiceNo;
                    layout.sourceBillingHistory.search({ invoice_no_string: invoiceNo });
                } else {
                    layout.textboxRebillBy.selectedItem = layout.textboxRebillBy.items[0];
                    const orderId = this.sourceOrders.activeRow.get("id");
                    layout.textboxNumber.text = orderId;
                    layout.sourceBillingHistory.search({ order_id: orderId });
                }
            },
            doAfterSlideOut: () => {
                if (layout.errorMessage.length > 0) {
                    const errorMessageString = layout.errorMessage.join(", "); // Join array elements into a single string
                    Snackbar.showWarningSnackbar(errorMessageString);
                }
                if (layout.successMessage) {
                    Toast.showSuccessToast(layout.successMessage);
                }
            }
        })
    }

    async convertAmount(amount: Currency, orderCurrencyCode: string): Promise<Currency> {
        const conversionRate = await CurrencyUtil.getConversionRate(amount.getConversionDate(), amount.getCurrencyCode(), orderCurrencyCode);
        const currencyProps: CurrencyProps = {
            amount: amount.getAmount() * conversionRate,
            currency_code: orderCurrencyCode,
            conversion_date: amount.getConversionDate()
        }
        return new Currency(currencyProps);
    }

    override displayInViewModeAfterLoad() {
        super.displayInViewModeAfterLoad();
        this.dataHeader.displayInViewMode();
        this.panelOrderTab.enabled = true;
        this.labelOrderView.enabled = true;
        this.panelMovementTab.enabled = true;
        this.labelMovementView.enabled = true;
        this.panelInvoiceHistoryTab.enabled = true;
        this.labelInvoiceHistory.enabled = true;
    }
}
